// §MVP-first / progressive disclosure — the founder's product model.
//
// The FIRST generation returns a LEAN app: it looks/behaves/functions amazingly
// but WITHOUT scaffolding (no forced sign-up, payments, or integrations). Then
// Pomela PROACTIVELY offers to add capabilities ONE AT A TIME — each accepted
// suggestion adds one piece, so the user keeps building and stays longer.
//
// This module is the deterministic brain for that flow. Given the app's prompt +
// name it returns an ORDERED list of capabilities the lean app doesn't have yet,
// each with (a) the OFFER copy, (b) the WHY (the benefit, never jargon), and
// (c) the exact revise INSTRUCTION the existing revise pipeline runs. No LLM —
// the list is static-by-archetype so it never off-contexts (the lesson from the
// "add a rest timer to a pizza menu" bug). The UI shows ONE primary suggestion
// at a time + a couple of alternates; accepting one re-runs gen in revise mode.

export interface Capability {
  /** Stable id — also the analytics key + the per-app "already offered" memo. */
  id: string;
  /** The proactive offer, imperative + short. "Add sign-in" / "Add a chart". */
  label: string;
  /** The benefit in plain language — WHY a user would want it. */
  why: string;
  /** The revise instruction handed to onRevise() verbatim. Specific, so the
   *  surgical/spec-diff revise adds exactly this one piece. */
  instruction: string;
  /** Lucide icon name for the chip. */
  icon: string;
  /** i18n keys for the displayed label + why (instruction stays the gen payload). */
  labelKey: string;
  whyKey: string;
}

interface CapabilityDomain {
  keywords: string[];
  caps: Capability[];
}

// Domain-specific capabilities — offered FIRST when the app matches, because a
// "menu → online ordering" beats a generic "add sign-in" for a restaurant.
const DOMAINS: CapabilityDomain[] = [
  {
    keywords: ['pizza', 'menu', 'restaurant', 'cafe', 'coffee', 'food', 'bakery', 'diner', 'burger', 'sushi', 'taco'],
    caps: [
      { id: 'ordering', label: 'Add online ordering', why: 'Let customers order and pay from the menu', instruction: 'Add online ordering: an add-to-cart on each menu item, a cart, and a checkout screen.', labelKey: 'cap.food_ordering', whyKey: 'cap.food_ordering.why', icon: 'shopping-bag' },
      { id: 'photos', label: 'Add item photos', why: 'Dishes with photos sell far better', instruction: 'Add a photo to each menu item.', labelKey: 'cap.food_photos', whyKey: 'cap.food_photos.why', icon: 'image' }
    ]
  },
  {
    keywords: ['workout', 'gym', 'fitness', 'exercise', 'training', 'reps', 'yoga', 'run', 'habit', 'streak', 'meditat'],
    caps: [
      { id: 'chart', label: 'Add a progress chart', why: 'See trends and stay motivated', instruction: 'Add a weekly progress chart that visualizes the logged data over time.', labelKey: 'cap.fit_chart', whyKey: 'cap.fit_chart.why', icon: 'trending-up' },
      { id: 'reminders', label: 'Add reminders', why: 'A nudge brings people back daily', instruction: 'Add a reminders setting so the user can schedule a daily nudge.', labelKey: 'cap.fit_reminders', whyKey: 'cap.fit_reminders.why', icon: 'bell' }
    ]
  },
  {
    keywords: ['shop', 'store', 'product', 'cart', 'commerce', 'boutique', 'catalog', 'sell'],
    caps: [
      { id: 'checkout', label: 'Add checkout', why: 'Turn browsing into sales', instruction: 'Add a cart and a checkout screen with an order summary.', labelKey: 'cap.shop_checkout', whyKey: 'cap.shop_checkout.why', icon: 'shopping-cart' },
      { id: 'payments', label: 'Accept payments', why: 'Get paid right in the app', instruction: 'Add an accept-payments flow on checkout (the connect-your-processor setup, no live keys).', labelKey: 'cap.shop_payments', whyKey: 'cap.shop_payments.why', icon: 'credit-card' }
    ]
  },
  {
    keywords: ['event', 'wedding', 'party', 'rsvp', 'invite', 'meetup', 'conference', 'club', 'community', 'group'],
    caps: [
      { id: 'rsvp', label: 'Add RSVP', why: 'Know who is coming', instruction: 'Add an RSVP form so guests can respond, with a running attendee count.', labelKey: 'cap.event_rsvp', whyKey: 'cap.event_rsvp.why', icon: 'user-check' },
      { id: 'share', label: 'Add invites', why: 'Let people invite their friends', instruction: 'Add a share/invite action so attendees can invite others.', labelKey: 'cap.event_share', whyKey: 'cap.event_share.why', icon: 'share-2' }
    ]
  },
  {
    keywords: ['course', 'lesson', 'quiz', 'study', 'learn', 'flashcard', 'vocab', 'class', 'student', 'homework'],
    caps: [
      { id: 'progress', label: 'Add progress tracking', why: 'Show how far along they are', instruction: 'Add lesson progress tracking with a completion indicator.', labelKey: 'cap.course_progress', whyKey: 'cap.course_progress.why', icon: 'check-circle' },
      { id: 'accounts', label: 'Add sign-in', why: 'So progress saves to their account', instruction: 'Add sign-in so each learner has their own saved progress.', labelKey: 'cap.course_accounts', whyKey: 'cap.course_accounts.why', icon: 'user' }
    ]
  }
];

// Universal capabilities — the scaffolding any lean app can grow into. Ordered
// by how often a real user wants them next.
const UNIVERSAL: Capability[] = [
  { id: 'accounts', label: 'Add sign-in', why: 'So each person’s data is their own', instruction: 'Add sign-in so users have their own accounts and their data is private to them.', labelKey: 'cap.u_accounts', whyKey: 'cap.u_accounts.why', icon: 'user' },
  { id: 'chart', label: 'Add insights', why: 'Visualize the data at a glance', instruction: 'Add an insights section with a chart that summarizes the app’s data.', labelKey: 'cap.u_chart', whyKey: 'cap.u_chart.why', icon: 'bar-chart-2' },
  { id: 'reminders', label: 'Add reminders', why: 'Bring people back', instruction: 'Add a reminders/notifications setting so users can schedule a nudge.', labelKey: 'cap.u_reminders', whyKey: 'cap.u_reminders.why', icon: 'bell' },
  { id: 'share', label: 'Add sharing', why: 'Let people share or collaborate', instruction: 'Add a share action so users can share the app or invite collaborators.', labelKey: 'cap.u_share', whyKey: 'cap.u_share.why', icon: 'share-2' },
  { id: 'search', label: 'Add search', why: 'Find anything fast', instruction: 'Add a search bar that filters the main list.', labelKey: 'cap.u_search', whyKey: 'cap.u_search.why', icon: 'search' }
];

/**
 * The ordered capabilities a lean app can grow into next — domain matches first,
 * then universal, de-duped by id. `alreadyAdded` (ids the user accepted on this
 * app) are filtered out so each offer is fresh and we never re-suggest a piece
 * that's already there. Returns at most `limit`.
 */
export function nextCapabilitiesFor(
  promptOrName: string,
  alreadyAdded: readonly string[] = [],
  limit = 3
): Capability[] {
  const hay = (promptOrName || '').toLowerCase();
  const domain = DOMAINS.find((d) => d.keywords.some((k) => hay.includes(k)));
  const seen = new Set<string>(alreadyAdded);
  const out: Capability[] = [];
  for (const c of [...(domain?.caps ?? []), ...UNIVERSAL]) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    out.push(c);
    if (out.length >= limit) break;
  }
  return out;
}
